import React from "react";
import styled from "styled-components";
import ImgSlider from "@/components/ImgSlider";
import Fade from "react-reveal/Fade";
import { useLanguage } from "../../contexts/LanguageContext";
import { getTranslation } from "../../translations/translations";

function AllProjects({
  leftBtnText,
  rightBtnText,
  title,
  extra,
  role,
  problems,
  solutions,
  img1,
  img2,
  img3,
  icon1,
  icon2,
  icon3,
  icon4,
  icon5,
  icon6,
  offline = false,
  hideImages = false,
}) {
  const { language } = useLanguage();

  return (
    <Wrap>
      <Fade top>
        <ItemText>
          <h1>{title}</h1>
          <Icons src={icon1} />
          <Icons src={icon2} />
          <Icons src={icon3} />
          <Icons src={icon4} />
          <Icons src={icon5} />
          <Icons src={icon6} />
        </ItemText>
        <p>{!extra ? null : extra}</p>
      </Fade>
      <ProjectContainer hideImages={hideImages}>
        <ProjectInfo hideImages={hideImages}>
          <h2>{getTranslation(language, "projectDetail.details")}</h2>
          <h4>{getTranslation(language, "projectDetail.role")}</h4>
          <p>{role}</p>
          <h4>{getTranslation(language, "projectDetail.problems")}</h4>
          <p>{problems}</p>
          <h4>{getTranslation(language, "projectDetail.solutions")}</h4>
          <p>{solutions}</p>
        </ProjectInfo>
        {!hideImages && (
          <SliderContainer>
            <ImgSlider FirstImg={img1} SecondImg={img2} ThirdImg={img3} />
          </SliderContainer>
        )}
      </ProjectContainer>
      <Buttons>
        <Fade bottom>
          <ButtonGroup>
            {offline
              ? null
              : leftBtnText && <LeftButton>{leftBtnText}</LeftButton>}
            {rightBtnText && <RightButton>{rightBtnText}</RightButton>}
          </ButtonGroup>
        </Fade>
      </Buttons>
    </Wrap>
  );
}

export default AllProjects;

const Wrap = styled.div`
  max-width: 100vw;
  min-height: 100vh;
  background-color: rgba(248, 241, 255, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
`;

const ItemText = styled.div`
  width: 100vw;
  padding-top: 12vh;
  justify-content: center;
  text-align: center;
  color: black;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;

  @media (max-width: 768px) {
    padding-top: 10vh;
    padding-left: 10px;
    padding-right: 10px;
  }

  h1 {
    margin-top: -7px;
    margin-right: 2px;
    @media (max-width: 768px) {
      font-size: 20px;
      margin-top: -4px;
      margin-right: 0;
    }
    @media (max-width: 480px) {
      font-size: 18px;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  margin: 15px 10px;
  cursor: pointer;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const LeftButton = styled.div`
  background-color: rgba(9, 21, 64, 0.8);
  height: 40px;
  width: 256px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 100px;
  opacity: 0.85;
  text-transform: uppercase;
  font-size: 12px;
  margin: 8px;
  transition: all 250ms ease;

  & a {
    color: white;
    text-decoration: none;
  }

  &:hover {
    background-color: rgba(9, 21, 64, 0.9);
  }
`;

const RightButton = styled(LeftButton)``;

const Buttons = styled.div`
  width: 100vw;
  display: flex;
  justify-content: center;
  background: rgba(48, 197, 255, 0.4);
  padding: 20px 0;
  @media (max-width: 768px) {
    max-width: 100%;
    margin-top: 20px;
    padding: 15px 0;
  }
`;

const ProjectContainer = styled.div`
  display: flex;
  justify-content: ${(props) => (props.hideImages ? "center" : "space-around")};
  margin: 0px 60px;
  height: ${(props) => (props.hideImages ? "auto" : "70vh")};
  width: 100vw;
  @media (max-width: 968px) {
    flex-direction: column;
    height: auto;
    margin: 0px 20px;
    padding: 20px 0;
  }
  @media (max-width: 768px) {
    margin: 0px 10px;
    padding: 10px 0;
  }
`;

const SliderContainer = styled.div`
  max-width: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;
  @media (max-width: 968px) {
    max-width: 100%;
    width: 100%;
    margin-bottom: 30px;
    padding: 0;
  }
`;

const ProjectInfo = styled.div`
  margin-left: ${(props) => (props.hideImages ? "0" : "45px")};
  width: ${(props) => (props.hideImages ? "80%" : "35%")};
  max-width: ${(props) => (props.hideImages ? "1000px" : "none")};
  max-height: 65vh;
  overflow-y: auto;
  color: black;

  @media (max-width: 1200px) {
    width: ${(props) => (props.hideImages ? "80%" : "30%")};
  }

  @media (max-width: 968px) {
    width: 100%;
    margin-left: 0;
    margin-top: 20px;
    max-height: none;
    overflow-y: visible;
  }

  @media (max-width: 768px) {
    margin-top: 10px;
    width: 100%;
  }

  h2 {
    margin-top: 30px;
    @media (max-width: 768px) {
      margin-top: 10px;
      font-size: 1.5rem;
    }
  }

  h4 {
    margin-top: 10px;
    font-weight: bold;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    @media (max-width: 768px) {
      padding: 2px;
      margin: 10px 0;
      font-size: 1rem;
      font-weight: bold;
    }
  }

  p {
    text-align: justify;
    font-size: 16px;
    line-height: 1.6;
    @media (max-width: 768px) {
      padding: 2px;
      margin: 0 0 15px 0;
      font-size: 14px;
    }
  }
`;

const Icons = styled.img`
  height: 25px;
  margin: 5px;
  @media (max-width: 768px) {
    height: 20px;
    margin: 3px;
  }
  @media (max-width: 480px) {
    height: 18px;
    margin: 2px;
  }
`;
